import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../utils/cn";
import { ease, duration, prefersReducedMotion } from "../../utils/motion";

/**
 * JPG Academy App Switcher (Waffle Grid)
 *
 * A Google-style "waffle" / "bento box" grid trigger that opens a dropdown
 * panel containing app tiles for navigating between JPG Academy applications.
 *
 * Uses relative/absolute positioning (no portal) to avoid SSR hydration
 * issues. The consuming layout must ensure the trigger's overflow ancestor
 * doesn't clip the dropdown — either by placing the trigger outside
 * `overflow-y-auto` containers or using `overflow-visible` on the wrapper.
 *
 * Features:
 * - 9-dot waffle grid trigger icon (universally recognized)
 * - Dropdown with branded app tiles in a responsive grid
 * - Audience-driven icon colors (admin=dark navy, employee=navy, parent=maroon, external=gold)
 * - Gold ring on the currently active app
 * - External audience apps open in a new tab
 * - Branded image icons with a string-icon resolver fallback
 * - Framer-motion entrance animation with staggered tiles
 * - Keyboard accessible (Escape to close, Tab through tiles)
 * - Click-outside dismiss
 * - Works in both header and sidebar contexts
 * - Respects prefers-reduced-motion
 *
 * Usage:
 *   // With @jpg/app-registry (icon names as strings):
 *   import { apps as platformApps } from '@jpg/app-registry'
 *
 *   <AppSwitcher
 *     apps={platformApps}
 *     currentAppId="required-services"
 *     iconResolver={(name) => {
 *       const Icon = iconMap[name]
 *       return Icon ? <Icon size={20} /> : null
 *     }}
 *   />
 *
 *   // With inline JSX icons (backward compatible):
 *   <AppSwitcher
 *     apps={[
 *       { id: 'x', name: 'My App', href: '/', icon: <SomeIcon />, audience: 'employee' },
 *     ]}
 *   />
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type AppAudience = "admin" | "employee" | "parent" | "external";

export interface AppSwitcherApp {
  /** Unique identifier for the app */
  id: string;
  /** Display name (rendered in system sans) */
  name: string;
  /** Navigation URL (full or relative) */
  href: string;
  /** Icon — either a React element or a string icon name resolved via iconResolver */
  icon: React.ReactNode | string;
  /** Optional branded app image. Takes precedence over the fallback icon. */
  iconSrc?: string;
  /** Who the app is for — drives color and link behavior. Falls back to 'employee'. */
  audience?: AppAudience;
  /** Optional short description */
  description?: string;
  /** Whether the app is accessible without authentication */
  public?: boolean;
}

export interface AppSwitcherProps {
  /** List of available apps */
  apps: AppSwitcherApp[];
  /** ID of the currently active app */
  currentAppId?: string;
  /** Visual variant — controls trigger style and dropdown position */
  variant?: "header" | "sidebar";
  /** Resolve string icon names to React elements */
  iconResolver?: (name: string) => React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Open change handler */
  onOpenChange?: (open: boolean) => void;
  /** Additional class for the trigger button */
  className?: string;
  /** Additional class for the dropdown panel */
  panelClassName?: string;
  /** Label shown above the grid */
  label?: string;
}

// ─── Audience → color maps ─────────────────────────────────────────────────

const iconBgMap: Record<AppAudience, string> = {
  admin:
    "bg-navy-800/15 text-navy-900 group-hover:bg-navy-800 group-hover:text-cream",
  employee: "bg-navy/10 text-navy group-hover:bg-navy group-hover:text-cream",
  parent:
    "bg-maroon/10 text-maroon group-hover:bg-maroon group-hover:text-cream",
  external: "bg-gold/15 text-gold group-hover:bg-gold group-hover:text-navy",
};

const activeRingMap: Record<AppAudience, string> = {
  admin: "ring-2 ring-navy-800/30",
  employee: "ring-2 ring-navy/30",
  parent: "ring-2 ring-maroon/30",
  external: "ring-2 ring-gold/40",
};

function isExternalLink(audience: AppAudience | undefined): boolean {
  return audience === "external";
}

// ─── Waffle Icon ────────────────────────────────────────────────────────────

function WaffleIcon({ size = 20 }: { size?: number }) {
  const dotSize = size * 0.1;
  const gap = size * 0.3;
  const center = size / 2;
  const offsets = [-gap, 0, gap];

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="currentColor"
      aria-hidden="true"
    >
      {offsets.map((dy) =>
        offsets.map((dx) => (
          <circle
            key={`${dx}-${dy}`}
            cx={center + dx}
            cy={center + dy}
            r={dotSize}
          />
        )),
      )}
    </svg>
  );
}

// ─── Tile animation variants ────────────────────────────────────────────────

const reduced = prefersReducedMotion();

const tileContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: reduced ? 0 : 0.04,
      delayChildren: reduced ? 0 : 0.05,
    },
  },
};

const tileItem = {
  hidden: { opacity: 0, scale: 0.9, y: 4 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: reduced
      ? { duration: 0 }
      : { ...ease.default, duration: duration.fast },
  },
};

// ─── Component ──────────────────────────────────────────────────────────────

export function AppSwitcher({
  apps,
  currentAppId,
  variant = "header",
  iconResolver,
  open: controlledOpen,
  onOpenChange,
  className,
  panelClassName,
  label = "JPG Academy Apps",
}: AppSwitcherProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close on Escape
  React.useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, setIsOpen]);

  // Close on outside click
  React.useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, setIsOpen]);

  const gridCols = apps.length <= 4 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Switch application"
        className={cn(
          "relative inline-flex items-center justify-center",
          "rounded-md p-1.5",
          "transition-colors duration-fast",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30 focus-visible:ring-offset-2",
          variant === "header"
            ? "text-cream/60 hover:text-cream hover:bg-navy-700/50"
            : "text-cream/50 hover:text-cream hover:bg-navy-700 w-8 h-8",
          isOpen &&
            (variant === "header"
              ? "text-cream bg-navy-700/50"
              : "text-cream bg-navy-700"),
          className,
        )}
      >
        <WaffleIcon size={variant === "sidebar" ? 18 : 20} />
      </button>

      {/* Dropdown panel — positioned absolutely, no portal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="dialog"
            aria-label={label}
            className={cn(
              "absolute z-popover",
              "bg-white rounded-xl shadow-lg",
              "overflow-hidden",
              "w-[280px] sm:w-[320px]",
              variant === "header" && "right-0 top-full mt-2",
              variant === "sidebar" && "left-full bottom-0 ml-2",
              panelClassName,
            )}
            style={{ color: "var(--color-navy)" }}
            initial={{
              opacity: 0,
              scale: 0.95,
              y: variant === "header" ? -4 : 0,
              x: variant === "sidebar" ? -4 : 0,
            }}
            animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={
              reduced
                ? { duration: 0 }
                : { ...ease.enter, duration: duration.fast }
            }
          >
            {/* Panel header */}
            <div className="px-4 pt-3 pb-2 border-b border-navy-100">
              <h3 className="font-display uppercase tracking-trajan-wide text-[10px] font-bold text-navy-400">
                {label}
              </h3>
            </div>

            {/* App grid */}
            <motion.div
              className={cn("grid gap-1 p-2", gridCols)}
              variants={tileContainer}
              initial="hidden"
              animate="visible"
            >
              {apps.map((app) => {
                const isActive = app.id === currentAppId;
                const audience = app.audience ?? "employee";
                const external = isExternalLink(audience);

                const resolvedIcon = app.iconSrc ? (
                  <img
                    src={app.iconSrc}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="h-full w-full rounded-lg object-cover"
                  />
                ) : typeof app.icon === "string" && iconResolver ? (
                  iconResolver(app.icon)
                ) : (
                  app.icon
                );

                return (
                  <motion.a
                    key={app.id}
                    href={app.href}
                    {...(external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    variants={tileItem}
                    className={cn(
                      "group flex flex-col items-center gap-1.5 rounded-lg p-3",
                      "transition-colors duration-fast",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40",
                      "hover:bg-cream/60",
                      isActive && "bg-cream/40",
                    )}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <span
                      className={cn(
                        "flex items-center justify-center",
                        "w-10 h-10 rounded-lg",
                        "transition-all duration-fast",
                        iconBgMap[audience],
                        isActive && activeRingMap[audience],
                      )}
                    >
                      {resolvedIcon}
                    </span>
                    <span
                      className={cn(
                        "font-ui text-xs font-medium text-center leading-tight",
                        "max-w-full truncate",
                        isActive ? "text-navy font-semibold" : "text-navy-500",
                      )}
                    >
                      {app.name}
                    </span>
                  </motion.a>
                );
              })}
            </motion.div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-navy-100 bg-cream/50">
              <span className="text-[10px] font-ui text-navy-300 block text-center">
                John Paul the Great Academy
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
